import { shadeColor } from '@codeleap/utils'
import { textVariant } from './Text'
import { createStyles } from '@codeleap/styles'
import { StyleRegistry } from '../styles'

export type AvatarComposition =
  'wrapper' |
  'image' |
  'initials' |
  'descriptionOverlay' |
  'description' |
  'icon' |
  'badgeIconWrapper' |
  'badgeIcon' |
  'touchable'

const createAvatarVariant = createStyles<AvatarComposition>

export const AvatarStyles = {
  default: createAvatarVariant((theme) => ({
    wrapper: {
      ...theme.presets.relative,
      height: theme.size.large,
      width: theme.size.large,
      cursor: 'pointer',
    },
    initials: {
      ...textVariant('p2'),
      color: theme.baseColors.onPrimarySolid300Heading,
      textWrap: 'nowrap',
    },
    image: {
      borderRadius: theme.radius.infinite,
      height: theme.size.large,
      width: theme.size.large,
      objectFit: 'cover',
    },
    badgeIconWrapper: {
      ...theme.presets.absolute,
      ...theme.presets.center,
      backgroundColor: theme.baseColors.onPrimarySolid300Heading,
      top: '50%',
      left: '50%',
      width: theme.values.iconSize[4],
      height: theme.values.iconSize[4],
      borderRadius: theme.radius.infinite,
      ...theme.effects.light,
    },
    badgeIcon: {
      width: theme.values.iconSize[1],
      height: theme.values.iconSize[1],
      color: theme.baseColors.primarySolid300,
      ...theme.presets.center,
    },
    icon: {
      width: theme.values.iconSize[2],
      height: theme.values.iconSize[2],
    },
    touchable: {
      ...theme.presets.fullWidth,
      ...theme.presets.fullHeight,
      ...theme.presets.center,
      backgroundColor: theme.baseColors.primarySolid300,
      borderRadius: theme.radius.infinite,
    },
    descriptionOverlay: {
      ...theme.presets.absolute,
      ...theme.presets.center,
      bottom: theme.spacing.value(0),
      left: theme.spacing.value(0),
      right: theme.spacing.value(0),
      backgroundColor: shadeColor(theme.baseColors.neutralSolid1000, 0, 0.2),
      borderRadius: theme.radius.infinite,
    },
    description: {
      ...textVariant('p3'),
      color: theme.baseColors.neutralSolid0,
    },
  })),
  round: createAvatarVariant((theme) => ({
    wrapper: {
      borderRadius: theme.radius.infinite,
    },
  })),
  tiny: createAvatarVariant((theme) => ({
    wrapper: {
      height: theme.size.small,
      width: theme.size.small,
    },
    image: {
      height: theme.size.small,
      width: theme.size.small,
    },
    initials: {
      ...textVariant('p5'),
    },
  })),
  small: createAvatarVariant(theme => ({
    wrapper: {
      height: theme.size.medium,
      width: theme.size.medium,
    },
    image: {
      height: theme.size.medium,
      width: theme.size.medium,
    },
  })),
  medium: createAvatarVariant(theme => ({
    wrapper: {
      height: theme.size.large,
      width: theme.size.large,
    },
  })),
  chatHeader: createAvatarVariant(theme => ({
    wrapper: {
      height: theme.size.large,
      width: theme.size.large,
    },
    initials: {
      fontSize: 12,
    },
  })),
  large: createAvatarVariant(theme => ({
    wrapper: {
      height: theme.size.large * 2,
      width: theme.size.large * 2,
    },
    initials: {
      ...textVariant('h3'),
      color: theme.baseColors.neutralSolid0,
      textWrap: 'nowrap',
    },
    image: {
      height: theme.size.large * 2,
      width: theme.size.large * 2,
    },
  })),
  huge: createAvatarVariant(theme => ({
    wrapper: {
      width: 120,
      height: 120,
    },
    initials: {
      ...textVariant('h1'),
      color: theme.baseColors.primarySolid50,
    },
    image: {
      width: 120,
      height: 120,
    },
    badgeIconWrapper: {
      bottom: 0,
      top: 'auto',
      right: 0,
      left: 'auto',
      backgroundColor: theme.baseColors.primarySolid50,
    },
  })),
}

StyleRegistry.registerVariants('Avatar', AvatarStyles)
